import React from "react";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";
import { FormValues, MessagePartInputExtended } from "../types";
import { SenderText } from "../BulkMessageReviewPage/SenderText";
import { SendOptionText } from "../BulkMessageReviewPage/SendOptionText";
import { formattedText } from "../utils/formattedSenderName";
import { i18n } from "@/i18n";
import {
  BulkMessageSenderInput,
  BulkMessageSendOptionInput,
} from "@/__generated__/graphql";
import { MessagePreview } from "@/components/MessagePreview";
import { useCounselors } from "@/hooks/useCounselors";

interface ComposeDisplayProps {
  sender: BulkMessageSenderInput | undefined;
  sendOption: BulkMessageSendOptionInput | undefined;
  messageParts: MessagePartInputExtended[];
  errors?: FieldErrors<FormValues>;
  senderName?: string;
  senderProfilePhoto?: { url?: string | null } | null;
  timeZone?: string;
}

export function ComposeDisplay({
  sender,
  sendOption,
  messageParts,
  errors,
  senderName,
  senderProfilePhoto,
  timeZone,
}: ComposeDisplayProps) {
  const { data: counselors, loading } = useCounselors();
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.review",
  });

  const senderText = formattedText(sender, counselors || []);

  return (
    <div className="grid grow grid-cols-3 gap-8">
      <div className="flex flex-col gap-8">
        <InputWrapper label={t("sendFrom")} error={errors?.sender}>
          <p className="text-sm text-muted-foreground">
            <SenderText sender={sender} />
          </p>
        </InputWrapper>
        <InputWrapper label={t("sendOptions")} error={errors?.sendOption}>
          <p className="text-sm text-muted-foreground">
            <SendOptionText sendOption={sendOption} timeZone={timeZone} />
          </p>
        </InputWrapper>
        <InputWrapper
          label={t("messagePreview.title")}
          error={errors?.sendOption}
        >
          <p className="text-sm text-muted-foreground">
            {t("messagePreview.description")}
          </p>
        </InputWrapper>
      </div>
      <div className="col-span-2 flex justify-start overflow-hidden">
        <MessagePreview
          messageParts={messageParts}
          className="mb-[-32px] w-full max-w-[700px]"
          senderName={
            senderName || (loading ? i18n.t("global.loading") : senderText)
          }
          senderProfilePhoto={senderProfilePhoto}
        />
      </div>
    </div>
  );
}
